import {createSlice, createAsyncThunk, PayloadAction} from '@reduxjs/toolkit';
import type {RootState} from '../redux/store';
import {applyCouponApi} from '../api/checkout.api';
// Define a type for the slice state

type GiftCouponList = {
  discountAmount?: number;
  couponDesc?: string;
  couponID?: string;
};

type CouponItemList = {
  itemIdentifier: string;
  discountAmount: number;
  extSellPrice: number;
  itemID: string;
  giftCouponList: GiftCouponList[] | [];
  quantity: number;
  sellPrice: number;
};

type ApplyCouponResponse = {
  statusCode: string;
  message: string;
  storeID: string;
  orderID: string;
  itemList: CouponItemList[];
  alreadyApplied?: boolean;
};
interface ApplyCouponState {
  loading: boolean;
  applyCouponResponse: ApplyCouponResponse | null;
  error: string;
  couponApplied: boolean;
  loaderType: string;
}

// Define the initial state using that type
const initialState: ApplyCouponState = {
  loading: false,
  applyCouponResponse: null,
  error: '',
  couponApplied: false,
  loaderType: '',
};

export const applyCoupon = createAsyncThunk(
  'checkout/applyCoupon',
  async (parameters: any, thunkApi) => {
    return applyCouponApi(parameters)
      .then(response => {
        return response;
      })
      .catch(error => {
        return thunkApi.rejectWithValue(error.response.data);
      });
  },
);

export const applyCouponSlice = createSlice({
  name: 'applyCouponSlice',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    resetCouponState: () => initialState,
    setCouponApplied: (state, action: PayloadAction<boolean>) => {
      state.couponApplied = action.payload;
    },
    setLoaderType: (state, action: PayloadAction<string>) => {
      state.loaderType = action.payload;
    },
    setAppliedCouponFromLS: (
      state,
      action: PayloadAction<ApplyCouponResponse>,
    ) => {
      state.applyCouponResponse = {...action.payload, alreadyApplied: true};
    },
  },
  extraReducers: builder => {
    builder.addCase(applyCoupon.pending, state => {
      state.loading = true;
    });
    builder.addCase(applyCoupon.fulfilled, (state, {payload}) => {
      state.applyCouponResponse = payload;
      state.loading = false;
    });
    builder.addCase(applyCoupon.rejected, (state, action) => {
      state.error = action.toString();
      state.loading = false;
    });
  },
});
export const {
  resetCouponState,
  setCouponApplied,
  setLoaderType,
  setAppliedCouponFromLS,
} = applyCouponSlice.actions;
// Other code such as selectors can use the imported `RootState` type

export const couponApplied = (state: RootState): boolean =>
  state.applyCouponReducer.couponApplied;
export const loaderType = (state: RootState): string =>
  state.applyCouponReducer.loaderType;
export const applyCouponResponse = (
  state: RootState,
): ApplyCouponResponse | null => state.applyCouponReducer.applyCouponResponse;

export default applyCouponSlice.reducer;
